import { useState, useEffect, useMemo, useCallback } from 'react'
import { motion } from 'framer-motion'
import EpisodeCard from '../components/EpisodeCard'
import SearchBar from '../components/SearchBar'
import Tag from '../components/Tag'
import EmptyState from '../components/EmptyState'
import Loading from '../components/Loading'
import SEO from '../utils/seo'
import { trackSearch } from '../utils/analytics'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const Episodes = () => {
  const [episodes, setEpisodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTags, setSelectedTags] = useState([])

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const response = await fetch(`${API_URL}/episodes`)
        const data = await response.json()
        setEpisodes(data)
      } catch (error) {
        console.error('Error fetching episodes:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchEpisodes()
  }, [])

  const allTags = useMemo(() => {
    const tags = new Set()
    episodes.forEach((ep) => ep.tags.forEach((tag) => tags.add(tag)))
    return Array.from(tags).sort()
  }, [episodes])

  const filteredEpisodes = useMemo(() => {
    let results = episodes

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      results = results.filter(
        (ep) =>
          ep.title.toLowerCase().includes(query) ||
          ep.summary.toLowerCase().includes(query) ||
          ep.tags.some((tag) => tag.toLowerCase().includes(query))
      )
    }

    if (selectedTags.length > 0) {
      results = results.filter((ep) =>
        selectedTags.every((selectedTag) => ep.tags.includes(selectedTag))
      )
    }

    return results.sort((a, b) => new Date(b.date) - new Date(a.date))
  }, [episodes, searchQuery, selectedTags])

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query)
      if (query) {
        trackSearch(query, filteredEpisodes.length)
      }
    },
    [filteredEpisodes.length]
  )

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedTags([])
  }

  if (loading) return <Loading />

  return (
    <>
      <SEO
        title="Episodes"
        description="Browse all episodes of Prompted Thoughts. Explore topics in Gen AI, app development, developer experience, and data."
      />

      <div className="section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-off-white mb-4">All Episodes</h1>
            <p className="text-lg text-gray-400">
              {episodes.length} episode{episodes.length !== 1 ? 's' : ''} and counting
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <div className="mb-6">
              <SearchBar onSearch={handleSearch} />
            </div>

            {allTags.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-400 mb-3">Filter by topic:</h3>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <Tag
                      key={tag}
                      tag={tag}
                      clickable
                      selected={selectedTags.includes(tag)}
                      onClick={() => toggleTag(tag)}
                    />
                  ))}
                </div>
              </div>
            )}

            {(searchQuery || selectedTags.length > 0) && (
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span>
                  Showing {filteredEpisodes.length} of {episodes.length} episodes
                </span>
                <button
                  onClick={clearFilters}
                  className="text-cyan hover:text-cyan/80 font-medium gradient-underline"
                >
                  Clear filters
                </button>
              </div>
            )}
          </motion.div>

          {filteredEpisodes.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEpisodes.map((episode, index) => (
                <EpisodeCard key={episode.slug} episode={episode} index={index} />
              ))}
            </div>
          ) : (
            <EmptyState
              title={episodes.length === 0 ? 'No episodes yet' : 'No episodes found'}
              message={episodes.length === 0 ? 'Episodes are coming soon!' : 'Try adjusting your search or filter criteria'}
              actionLabel={episodes.length > 0 ? 'Clear filters' : undefined}
              onAction={episodes.length > 0 ? clearFilters : undefined}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default Episodes
